import type { MutableRefObject } from "react";
import { useCallback, useRef, useState } from "react";

import type { LocalAudioRecordingBlobRecord } from "@/domains/dexie-db/models/audio-recording";
import { audioRecordingModule } from "@/modules/audio-recording";

export type CapturedAudioPlayback = {
  audioElementRef: MutableRefObject<HTMLAudioElement | null>;
  playbackUrl: string | null;
  play: () => Promise<void>;
  clear: () => void;
  isPreparing: boolean;
  error: string | null;
  setError: (value: string | null) => void;
};

export const useCapturedAudioPlayback = (
  blobs: LocalAudioRecordingBlobRecord[]
): CapturedAudioPlayback => {
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const playbackUrlRef = useRef<string | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revokePlaybackUrl = useCallback(() => {
    const element = audioElementRef.current;
    if (element) {
      element.pause();
      element.removeAttribute("src");
      element.load();
    }

    const current = playbackUrlRef.current;
    if (current) {
      URL.revokeObjectURL(current);
      playbackUrlRef.current = null;
    }

    setPlaybackUrl(null);
  }, []);

  const clear = useCallback(() => {
    revokePlaybackUrl();
    setError(null);
  }, [revokePlaybackUrl]);

  const play = useCallback(async () => {
    if (!blobs.length) {
      setError("Record audio before attempting playback.");
      return;
    }

    revokePlaybackUrl();
    setIsPreparing(true);
    setError(null);

    try {
      const playbackBlob = await audioRecordingModule.transformBlobToPlayableFormat({
        blobs: blobs.map(blob => blob.rawBlob),
      });

      if (!playbackBlob) {
        setError("Failed to play recorded audio.");
        revokePlaybackUrl();
        return;
      }

      const url = URL.createObjectURL(playbackBlob);
      playbackUrlRef.current = url;
      setPlaybackUrl(url);
    } catch (unknownErr) {
      const message =
        unknownErr instanceof Error ? unknownErr.message : "Failed to play recorded audio.";
      setError(message);
      revokePlaybackUrl();
    } finally {
      setIsPreparing(false);
    }
  }, [blobs, revokePlaybackUrl]);

  return {
    audioElementRef,
    playbackUrl,
    play,
    clear,
    isPreparing,
    error,
    setError,
  };
};
